import Paginate from 'components/_UI/Paginate'
import Text from 'components/_UI/Text'
import React from 'react'
import { ArrowDownCircle } from 'react-feather'
import styled from 'styled-components'
import { Column } from 'styled/alignment/Column'
import colors from 'theme/colors'
import fonts from 'theme/fonts'
import breakpoints from 'theme/breakpoints'

const STATUS_MAP = {
    approved: { label: 'Aprovado', bg: '#DCFCE7', color: '#166534' },
    completed: { label: 'Concluído', bg: '#DCFCE7', color: '#166534' },
    accepted: { label: 'Aceito', bg: '#DCFCE7', color: '#166534' },
    pending: { label: 'Pendente', bg: '#FEF3C7', color: '#92400E' },
    canceled: { label: 'Expirado', bg: '#FEE2E2', color: '#DC2626' },
    failed: { label: 'Negado', bg: '#FEE2E2', color: '#DC2626' },
    rejected: { label: 'Recusado', bg: '#FEE2E2', color: '#DC2626' },
    refunded: { label: 'Estornado', bg: '#F3F4F6', color: '#374151' },
    chargeback: { label: 'Chargeback', bg: '#FEE2E2', color: '#991B1B' },
};

export default function WithdrawList({ data, perPage, totalItems, onPaginate, lastPage, currentPage }) {
    const items = data?.data?.data || [];

    function formatDate(dateStr) {
        const d = new Date(dateStr);
        return d.toLocaleDateString('pt-BR');
    }

    function formatTime(dateStr) {
        const d = new Date(dateStr);
        return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    }

    function formatValue(value) {
        return `R$ ${parseFloat(value).toFixed(2).replace('.', ',')}`;
    }

    return (
        <Container>
            <SectionHeader>
                <SectionIcon>
                    <ArrowDownCircle size={18} color={colors.occasionalPurple} />
                </SectionIcon>
                <HeaderContent>
                    <Text type='bold' color={colors.nightDark} paddingBottom={0}>
                        Histórico de saques
                    </Text>
                    <Text name='small' color={colors.night} paddingBottom={0}>
                        Acompanhe seus saques realizados
                    </Text>
                </HeaderContent>
            </SectionHeader>

            {/* Desktop table */}
            <DesktopTable>
                <thead>
                    <tr>
                        <Th>ID</Th>
                        <Th>Data</Th>
                        <Th>Valor</Th>
                        <Th>Status</Th>
                    </tr>
                </thead>
                <tbody>
                    {items.map(item => {
                        const statusInfo = STATUS_MAP[item?.status] || STATUS_MAP.pending;

                        return (
                            <Tr key={item?.id}>
                                <Td>
                                    <IdText>#{item?.id}</IdText>
                                </Td>
                                <Td>
                                    <DatePrimary>{formatDate(item?.created_at)}</DatePrimary>
                                    <DateSecondary>{formatTime(item?.created_at)}</DateSecondary>
                                </Td>
                                <Td>
                                    <ValueText>{formatValue(item?.value)}</ValueText>
                                </Td>
                                <Td>
                                    <StatusBadge bgColor={statusInfo.bg} textColor={statusInfo.color}>
                                        {statusInfo.label}
                                    </StatusBadge>
                                </Td>
                            </Tr>
                        );
                    })}
                </tbody>
            </DesktopTable>

            {/* Mobile cards */}
            <MobileCards>
                {items.map(item => {
                    const statusInfo = STATUS_MAP[item?.status] || STATUS_MAP.pending;

                    return (
                        <MobileCard key={item?.id}>
                            <MobileCardHeader>
                                <IdText>#{item?.id}</IdText>
                                <StatusBadge bgColor={statusInfo.bg} textColor={statusInfo.color}>
                                    {statusInfo.label}
                                </StatusBadge>
                            </MobileCardHeader>

                            <MobileCardBody>
                                <MobileField>
                                    <MobileLabel>Data</MobileLabel>
                                    <MobileValue>
                                        {formatDate(item?.created_at)} - {formatTime(item?.created_at)}
                                    </MobileValue>
                                </MobileField>
                                <MobileField>
                                    <MobileLabel>Valor</MobileLabel>
                                    <MobileValue style={{ fontWeight: 600 }}>
                                        {formatValue(item?.value)}
                                    </MobileValue>
                                </MobileField>
                            </MobileCardBody>
                        </MobileCard>
                    );
                })}
            </MobileCards>

            {lastPage > 1 && (
                <PaginateWrapper>
                    <Paginate
                        perPage={perPage}
                        onPaginate={onPaginate}
                        totalItems={totalItems}
                        lastPage={lastPage}
                        firstPage={1}
                        currentPage={currentPage}
                        marginTop={'0'}
                    />
                </PaginateWrapper>
            )}
        </Container>
    );
}

/* ── Layout ────────────────────────────────────── */

const Container = styled.div`
    min-height: 300px;
    display: flex;
    flex-direction: column;
    background: ${colors.neutralLight};
    border: 1px solid ${colors.neutralDark};
    border-radius: 12px;
    margin-top: 10px;
    overflow: hidden;
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 20px 24px 0;
    margin-bottom: 16px;
`;

const SectionIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: #F3EAFF;
    flex-shrink: 0;
`;

const HeaderContent = styled.div`
    flex: 1;
`;

const PaginateWrapper = styled.div`
    padding: 16px 24px;
    border-top: 1px solid ${colors.neutralDark};
`;

/* ── Desktop Table ─────────────────────────────── */

const DesktopTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    display: none;

    @media screen and (min-width: ${breakpoints.lg}) {
        display: table;
    }
`;

const Th = styled.th`
    text-align: left;
    padding: 14px 16px;
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    color: ${colors.night};
    text-transform: uppercase;
    letter-spacing: 0.4px;
    border-bottom: 1px solid ${colors.neutralDark};
    white-space: nowrap;

    &:first-child {
        padding-left: 24px;
    }

    &:last-child {
        padding-right: 24px;
    }
`;

const Tr = styled.tr`
    transition: background 0.15s ease;

    &:not(:last-child) {
        border-bottom: 1px solid ${colors.neutralDark};
    }

    &:hover {
        background: ${colors.neutral};
    }
`;

const Td = styled.td`
    padding: 14px 16px;
    vertical-align: middle;

    &:first-child {
        padding-left: 24px;
    }

    &:last-child {
        padding-right: 24px;
    }
`;

/* ── Cell Content ──────────────────────────────── */

const IdText = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const DatePrimary = styled.span`
    display: block;
    font-size: 14px;
    color: ${colors.nightDark};
`;

const DateSecondary = styled.span`
    display: block;
    font-size: ${fonts.sizes.small};
    color: ${colors.night};
    margin-top: 2px;
`;

const ValueText = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const StatusBadge = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 4px 12px;
    border-radius: 50px;
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    letter-spacing: 0.2px;
    white-space: nowrap;
    background: ${({ bgColor }) => bgColor};
    color: ${({ textColor }) => textColor};
`;

/* ── Mobile Cards ──────────────────────────────── */

const MobileCards = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 12px 16px;

    @media screen and (min-width: ${breakpoints.lg}) {
        display: none;
    }
`;

const MobileCard = styled.div`
    border: 1px solid ${colors.neutralDark};
    border-radius: 10px;
    padding: 16px;
    transition: all 0.15s ease;

    &:hover {
        background: ${colors.neutral};
        border-color: ${colors.nightLight};
    }
`;

const MobileCardHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    padding-bottom: 12px;
    border-bottom: 1px solid ${colors.neutralDark};
`;

const MobileCardBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const MobileField = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const MobileLabel = styled.span`
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    color: ${colors.night};
`;

const MobileValue = styled.span`
    font-size: 13px;
    color: ${colors.nightDark};
`;
